<template>
  <div data-testid="pre-processor-trail-row" class="pre-processor-trail-row">
    <button
      type="button"
      class="summary-toggle"
      :aria-expanded="expanded"
      :aria-controls="panelId"
      data-testid="pre-processor-trail-summary-toggle"
      @click="expanded = !expanded"
    >
      <span class="summary-text">
        <span class="summary-primary" data-testid="pre-processor-trail-summary-primary">
          {{ summaryPrimary }}
        </span>
        <span
          v-if="summarySecondary.length > 0"
          class="summary-secondary"
          :title="summarySecondary"
          data-testid="pre-processor-trail-summary-secondary"
        >
          {{ summarySecondary }}
        </span>
        <span
          v-if="feedSourcesLine"
          class="summary-feed-sources"
          :title="feedSourcesLine"
          data-testid="pre-processor-trail-feed-sources"
        >
          {{ feedSourcesLine }}
        </span>
      </span>
      <v-icon
        :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        size="small"
        class="summary-chevron"
      />
    </button>

    <div
      v-if="expanded"
      :id="panelId"
      class="expanded-panel mt-2"
      data-testid="pre-processor-trail-expanded-panel"
    >
      <v-alert
        v-if="explanations.length === 0"
        type="info"
        variant="tonal"
        density="comfortable"
        data-testid="pre-processor-trail-empty"
      >
        No additional inventory preparation details captured for this run.
      </v-alert>

      <ul
        v-else
        class="ma-0 pa-0 explanation-list"
        data-testid="pre-processor-trail-explanation-list"
      >
        <li
          v-for="(explanation, idx) in explanations"
          :key="`reason:${idx}`"
          class="explanation-item"
          :data-testid="`pre-processor-trail-explanation-item-${idx}`"
        >
          {{ explanation }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Phase 10.4.y — PreProcessorDecisionTrailRow.
 *
 * Pure presentational component for source-written `preProcessor`
 * VIN-trail records. It summarizes the action-specific outcome using
 * bounded record hints and lazily reveals writer-provided explanation
 * strings. It never renders raw provenance maps, JUID internals,
 * bucket names, S3 keys, or transform identifiers.
 */
import { computed, ref } from 'vue'
import {
  publishFlowPreProcessorActionCopy,
  publishFlowPreProcessorErrorCopy,
} from '@/utils/publishFlowStageLabels'
import { formatFeedSource } from '@/utils/feedSourceFormat'
import type {
  PublishDecisionRecord,
  PublishDecisionReason,
} from '@/services/publishPreviewArtifactsService'

const props = defineProps<{
  record: PublishDecisionRecord
}>()

const panelId = `pre-processor-trail-panel-${Math.random().toString(36).slice(2, 9)}`
const expanded = ref(false)

const summaryPrimary = computed<string>(() => {
  const status = typeof props.record.status === 'string' ? props.record.status : ''
  if (status === 'error') return publishFlowPreProcessorErrorCopy()
  if (status === 'changed') return publishFlowPreProcessorActionCopy(props.record.provenance ?? {})
  if (status === 'skipped') return 'Blocked'
  return 'Outcome unknown'
})

const summarySecondary = computed<string>(() => explanations.value[0] ?? '')

const feedSourcesLine = computed<string>(() => {
  const feeds = Array.isArray(props.record.feedSources)
    ? props.record.feedSources
        .filter((feed): feed is string => typeof feed === 'string' && feed.trim().length > 0)
        .map((feed) => feed.trim())
    : []
  const uniqueFeeds = Array.from(new Set(feeds))
  if (uniqueFeeds.length === 0) return ''
  const label = uniqueFeeds.length === 1 ? 'Source feed' : 'Source feeds'
  return `${label}: ${uniqueFeeds.map(formatFeedSource).join(', ')}`
})

const explanations = computed<string[]>(() => {
  const raw = props.record.reasons
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const explanation = (entry as PublishDecisionReason).explanation
    if (typeof explanation !== 'string') continue
    const trimmed = explanation.trim()
    if (trimmed.length > 0) out.push(trimmed)
  }
  return out
})
</script>

<style scoped>
.pre-processor-trail-row {
  /* Parent v-list-item supplies vertical rhythm. */
}
.summary-toggle {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.summary-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.summary-primary {
  font-weight: 500;
  font-size: 13px;
}
.summary-secondary {
  display: block;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.72);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-feed-sources {
  display: block;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.72);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-chevron {
  flex: 0 0 auto;
}
.expanded-panel {
  padding-left: 2px;
}
.explanation-list {
  list-style: none;
}
.explanation-item {
  font-size: 12px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.78);
}
.explanation-item + .explanation-item {
  margin-top: 6px;
}
</style>
